import { buildContentNodes, createCloseButton } from './block-builder';
import type { GuideStep, WalkthroughCallbacks } from './types';

/**
 * Construye el subtree DOM de un step de walkthrough con navegación
 * (counter, dots, contenido, botones atrás/siguiente/finalizar/omitir).
 *
 * Aplica las mismas reglas de seguridad que `buildStepContent`:
 *   - `textContent` para todo string del backend (cero `innerHTML`).
 *   - `imageUrl` validada con `isSafeUrl`.
 *
 * El botón primario cambia su label según el step:
 *   - último step → "Finalizar" → llama `onComplete`
 *   - otro       → `step.ctaText` o "Siguiente" → llama `onNext`
 *
 * El botón "Atrás" solo se pinta a partir del segundo step. El botón
 * "Omitir" siempre se pinta — el usuario debe poder salir en cualquier
 * momento.
 */
export function buildWalkthroughStepContent(
  step: GuideStep,
  stepIndex: number,
  totalSteps: number,
  doc: Document,
  callbacks: WalkthroughCallbacks,
): HTMLElement {
  const container = doc.createElement('div');
  container.className = 'veo-guide-content';

  const counter = doc.createElement('div');
  counter.className = 'veo-walkthrough-counter';
  counter.textContent = `Paso ${stepIndex + 1} de ${totalSteps}`;
  container.appendChild(counter);

  const progress = doc.createElement('div');
  progress.className = 'veo-walkthrough-progress';
  for (let i = 0; i < totalSteps; i++) {
    const dot = doc.createElement('span');
    dot.className = 'veo-walkthrough-progress-dot';
    if (i < stepIndex) dot.classList.add('completed');
    if (i === stepIndex) dot.classList.add('active');
    progress.appendChild(dot);
  }
  container.appendChild(progress);

  // Contenido del paso: respeta blocks[] (imágenes, rich text, full-bleed) o
  // cae a los escalares. Los bloques `button` se omiten — la navegación
  // (atrás/siguiente/omitir) es del chrome del tour, abajo.
  for (const node of buildContentNodes(step, doc)) {
    container.appendChild(node);
  }

  const actions = doc.createElement('div');
  actions.className = 'veo-walkthrough-actions';

  const skipBtn = doc.createElement('button');
  skipBtn.type = 'button';
  skipBtn.className = 'veo-walkthrough-skip';
  skipBtn.textContent = 'Omitir';
  skipBtn.addEventListener('click', () => callbacks.onSkip());
  actions.appendChild(skipBtn);

  const rightGroup = doc.createElement('div');
  rightGroup.className = 'veo-walkthrough-actions-right';

  if (stepIndex > 0) {
    const backBtn = doc.createElement('button');
    backBtn.type = 'button';
    backBtn.className = 'veo-walkthrough-btn-secondary';
    backBtn.textContent = 'Atrás';
    backBtn.addEventListener('click', () => callbacks.onBack());
    rightGroup.appendChild(backBtn);
  }

  const isLastStep = stepIndex === totalSteps - 1;
  const primaryBtn = doc.createElement('button');
  primaryBtn.type = 'button';
  primaryBtn.className = 'veo-guide-cta';
  const defaultLabel = isLastStep ? 'Finalizar' : 'Siguiente';
  primaryBtn.textContent =
    typeof step.ctaText === 'string' && step.ctaText ? step.ctaText : defaultLabel;
  primaryBtn.addEventListener('click', () => {
    if (isLastStep) callbacks.onComplete();
    else callbacks.onNext();
  });
  rightGroup.appendChild(primaryBtn);

  actions.appendChild(rightGroup);
  container.appendChild(actions);

  // "✕" universal (esquina superior derecha): salir en cualquier momento.
  // Mismo efecto que "Omitir" → reporta `dismissed`.
  container.appendChild(createCloseButton(doc, () => callbacks.onSkip()));

  return container;
}
